import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import '../styles/BlogPrimitives.css';

const BLOG_POST_CANONICAL_URL =
  'https://circumvent.io/blog/stop-trying-to-fit-new-technology-into-old-categories';
const BLOG_POST_PUBLISHED_AT = '2026-08-04';
const BLOG_POST_READ_TIME = '6 min read';
const BLOG_POST_AUTHOR = 'Michael Watts';
const FEATURE_IMAGE_SRC = '/images/blog/stop-fitting-new-tech-into-old-categories.png';
const BLOG_POST_OG_TITLE = 'Stop Trying to Fit New Technology Into Old Categories | Circumvent';
const BLOG_POST_OG_DESCRIPTION =
  "Why every breakthrough product gets misunderstood before it gets adopted, and what building Cloud Conformity and Circumvent taught me about categories, bottlenecks, and reasoning at scale.";
const BLOG_POST_OG_IMAGE_URL =
  'https://circumvent.io/images/blog/stop-fitting-new-tech-into-old-categories.png';

const BlogNewTechOldCategoriesPage = () => (
  <>
    <Helmet>
      <title>{BLOG_POST_OG_TITLE}</title>
      <meta name="description" content={BLOG_POST_OG_DESCRIPTION} />
      <link rel="canonical" href={BLOG_POST_CANONICAL_URL} />
    </Helmet>
    <SeoSocialTags
      title={BLOG_POST_OG_TITLE}
      description={BLOG_POST_OG_DESCRIPTION}
      url={BLOG_POST_CANONICAL_URL}
      ogType="article"
      articlePublishedTime={`${BLOG_POST_PUBLISHED_AT}T00:00:00.000Z`}
      ogImageUrl={BLOG_POST_OG_IMAGE_URL}
    />

    <main className="page page--light blog-post-page">
      <section className="page-section page-section--white" aria-labelledby="post-heading">
        <article className="page-container blog-post">
          <div className="blog-post__header">
            <Link to="/blog" className="blog-crumb-link">
              <span aria-hidden="true">←</span> Blog
            </Link>
            <h1 id="post-heading" className="blog-post__title">
              Stop Trying to Fit New Technology Into Old Categories
            </h1>
            <p className="blog-post__lead">
              Why every breakthrough product gets misunderstood before it gets adopted.
            </p>

            <ul className="blog-meta-list blog-meta-list--header" aria-label="Post details">
              <li>Perspective</li>
              <li>
                <time dateTime={BLOG_POST_PUBLISHED_AT}>4 Aug 2026</time>
              </li>
              <li>{BLOG_POST_READ_TIME}</li>
              <li>By {BLOG_POST_AUTHOR}</li>
            </ul>
          </div>

          <div className="blog-post__layout">
            <div className="blog-post__content">
              <figure className="blog-post__feature-image blog-post__feature-image--bleed">
                <img
                  src={FEATURE_IMAGE_SRC}
                  alt="A confused businessman stands between signposts for CNAPP, SIEM, ASPM, Exposure Management, and Vulnerability Management, holding a Circumvent sign, next to handwritten text reading 'Where do you fit?'"
                  width={1536}
                  height={1024}
                  loading="eager"
                  decoding="async"
                />
              </figure>

              <p>
                One of the hardest parts of building a genuinely new product isn&apos;t the technology. It&apos;s convincing people to stop evaluating it as though it&apos;s an old one.
              </p>
              <p>
                One of the first questions I get when someone hears about Circumvent is almost always:
              </p>
              <p>&quot;So... are you a CNAPP?&quot;</p>
              <p>Or: &quot;Is this SIEM?&quot;</p>
              <p>Or: &quot;Is it Exposure Management?&quot;</p>
              <p>
                I understand why. Executives don&apos;t wake up wanting another category. They have budgets organized around existing categories. Analysts have Magic Quadrants. Buyers have evaluation criteria. Everyone is trying to reduce uncertainty by comparing something new with something they already understand.
              </p>
              <p>
                It&apos;s a perfectly rational instinct. It&apos;s also one of the easiest ways to misunderstand a genuinely new product.
              </p>

              <h2 id="we-did-this-before">We did this before</h2>
              <p>
                This isn&apos;t the first time I&apos;ve experienced it. When we started Cloud Conformity almost ten years ago, customers didn&apos;t know they needed continuous cloud configuration monitoring.
              </p>
              <p>
                CSPM wasn&apos;t yet a recognized category. We weren&apos;t network security. We weren&apos;t infrastructure security. We simply believed that if organizations were moving to the cloud, someone needed to continuously check whether their environments remained secure.
              </p>
              <p>
                That seems obvious today. It certainly wasn&apos;t in 2016. For years, the conversation wasn&apos;t about whether the problem existed; it was about trying to work out which existing bucket we belonged in.
              </p>
              <p>
                Eventually, Gartner&apos;s Cloud Security Posture Management (CSPM) category became widely recognized, helped in no small part by a steady stream of high-profile S3 bucket breaches that made cloud misconfiguration impossible to ignore. The category arrived after companies had already started solving the problem. That&apos;s usually how innovation works.
              </p>

              <h2 id="categories-are-a-lagging-indicator-of-innovation">
                Categories are a lagging indicator of innovation
              </h2>
              <p>
                I love categories. Seriously. They help buyers compare products. They create shared language. They make procurement easier.
              </p>
              <p>
                But categories are a lagging indicator of innovation. A category only forms once enough companies are solving roughly the same problem in roughly the same way. If you&apos;re building something genuinely new, there&apos;s a good chance no category fits yet, and trying too hard to force one becomes a distraction.
              </p>
              <p>
                That&apos;s not because buyers are closed-minded. Buying enterprise software is fundamentally an exercise in reducing risk. Existing categories come with established budgets, procurement processes, analyst reports, peer references and implementation playbooks. Choosing something outside those categories requires conviction. And conviction is scarce.
              </p>

              <h2 id="the-temptation">The temptation</h2>
              <p>
                As an early-stage startup, the pressure to fit somewhere is constant. Investors ask what your category is. Customers ask who you compete with. Partners ask which budget you come from.
              </p>
              <p>
                Eventually you start adjusting your language just to make the conversations easier. You become &quot;like X, but with AI.&quot; Or whatever analogy makes people nod.
              </p>
              <p>
                The problem is that every shortcut quietly changes how people evaluate you. Say you&apos;re &quot;another CNAPP&quot; and they&apos;ll compare your detections. Say &quot;ASPM&quot; and they&apos;ll compare your inventory. Say &quot;vulnerability management&quot; and they&apos;ll compare your scanners. Before long, you&apos;re being measured against products solving a completely different problem.
              </p>

              <h2 id="technology-doesnt-improve-the-old-answer-it-moves-the-question">
                Technology doesn&apos;t improve the old answer. It moves the question.
              </h2>
              <p>
                Here&apos;s the pattern I keep seeing: new technology rarely just makes the existing solution better. More often, it solves the old bottleneck well enough that a new one appears somewhere downstream.
              </p>
              <p>
                Cloud Conformity, along with plenty of other great companies, helped make cloud misconfiguration detection continuous and scalable. The industry became incredibly good at finding problems. Detection stopped being the bottleneck.
              </p>
              <p>
                Today, most large enterprises don&apos;t struggle to find security issues. They struggle to know which ones matter. Millions of findings. Thousands of services. Hundreds of engineering teams. Dozens of security products. The scarce resource isn&apos;t another alert; it&apos;s engineering attention.
              </p>
              <p>
                Every generation of technology creates a new bottleneck this way. Cloud created the need for CSPM. Containers created a new generation of runtime and supply chain security tools. Identity became the new perimeter. Each time, the industry became better at answering the old question just as a new, harder question came into view.
              </p>
              <p>Today, we&apos;re seeing that happen again.</p>

              <h2 id="what-that-looks-like-in-practice">What that looks like in practice</h2>
              <p>
                Imagine a critical CVE is disclosed in an open-source library your organization uses. Within minutes, your security scanner flags it. Severity 9.1. Patch available.
              </p>
              <p>Detection wasn&apos;t the hard part. Now the real work begins.</p>
              <p>
                Is the vulnerable function actually reachable, or is it sitting in code that&apos;s never executed? Is the affected service internet-facing, or buried inside an internal batch process? Does it process sensitive customer data, or is it supporting a non-critical internal tool? Who owns the service? Who introduced the dependency? Will upgrading introduce breaking API changes, dependency conflicts, or require weeks of regression testing?
              </p>
              <p>
                None of those questions are answered by the scanner. Today, a security engineer has to piece the answers together manually. Tracing call paths, checking runtime exposure, finding the owning team, reviewing change history, reading release notes and trying to understand the operational impact of the fix.
              </p>
              <p>
                The scanner identified the CVE in minutes. Working out whether it actually matters and what fixing it will cost can take days.
              </p>
              <p>
                That&apos;s not a detection problem. It&apos;s a reasoning problem. And that&apos;s exactly why existing categories struggle to describe it. Most security categories were built around detecting problems. The next generation of technology is enabling us to solve what happens after detection.
              </p>
              <p>
                Instead of simply generating another alert, systems can now reason across source code, cloud infrastructure, runtime telemetry, ownership, business criticality and historical change data to answer the questions that determine what should happen next: patch immediately, defer until the next maintenance window, route it to the correct engineering team, or determine that, while technically vulnerable, the code path isn&apos;t reachable and the immediate risk is negligible.
              </p>
              <p>
                Not more alerts. The reasoning that application security and cloud security engineers have always had to do manually, one finding at a time.
              </p>

              <h2 id="thats-the-problem-were-solving">That&apos;s the problem we&apos;re solving</h2>
              <p>
                We don&apos;t think the industry needs another product that finds problems. We think it needs one that understands them.
              </p>
              <p>
                Recent advances in large language models and agentic systems finally make that possible. Rather than simply identifying risks, they can investigate findings, understand business context, determine ownership, prioritize what actually matters and increasingly help remediate issues. The same reasoning process an experienced security engineer performs every day.
              </p>
              <p>
                That&apos;s the problem Circumvent is solving. It&apos;s a different problem from the ones existing categories were built to describe. Which means it probably deserves different language.
              </p>

              <h2 id="the-lesson">The lesson</h2>
              <p>
                One of the biggest lessons from building two companies is this: don&apos;t define your company by the categories that already exist. Define it by the problem that now exists because the technology has changed.
              </p>
              <p>
                Eventually the market will decide what to call it. Until then, your job isn&apos;t to squeeze into someone else&apos;s box, it&apos;s to help customers understand why the box no longer fits.
              </p>
              <p>
                If I&apos;ve learned anything from building Cloud Conformity and now Circumvent, it&apos;s that categories don&apos;t create markets. Technology changes. New bottlenecks appear. Builders solve them. The category comes later.
              </p>
              <p>
                The irony is that if you succeed, someone will eventually give the problem a name. By then, the category will feel obvious. Just like CSPM does today.
              </p>

              <Link to="/blog" className="blog-crumb-link">
                <span aria-hidden="true">←</span> Blog
              </Link>
            </div>

            <aside className="blog-post__sidebar" aria-label="Post navigation">
              <nav className="blog-toc" aria-label="On this page">
                <p className="blog-toc__title">On this page</p>
                <ul className="blog-toc__list">
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#we-did-this-before">
                      We did this before
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#categories-are-a-lagging-indicator-of-innovation">
                      Categories are a lagging indicator of innovation
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#the-temptation">
                      The temptation
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a
                      className="blog-toc__link"
                      href="#technology-doesnt-improve-the-old-answer-it-moves-the-question"
                    >
                      Technology doesn&apos;t improve the old answer. It moves the question.
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#what-that-looks-like-in-practice">
                      What that looks like in practice
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#thats-the-problem-were-solving">
                      That&apos;s the problem we&apos;re solving
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#the-lesson">
                      The lesson
                    </a>
                  </li>
                </ul>
              </nav>
            </aside>
          </div>
        </article>
      </section>
    </main>
  </>
);

export default BlogNewTechOldCategoriesPage;
